import { useMemo, useState } from "react";
import { BellRing, Check } from "lucide-react";
import { toast } from "sonner";
import { subscribeGapNotify } from "../lib/api";

// Grey INSUFFICIENT_DATA card capture — "email me when this combo is verified".
export default function GapCaptureBox({ result }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const meds = Array.isArray(result?.medications) ? result.medications : [];
  const med = meds[0] || result?.medication || {};
  const medName = useMemo(() => {
    if (meds.length > 1) return meds.map((m) => m.name).join(" + ");
    return med.name || med.generic_name || "your medication";
  }, [meds, med]);

  const { ingredientIds, ingredientNames } = useMemo(() => {
    const ids = [];
    const names = [];
    const push = (id, name) => {
      if (id && !ids.includes(id)) { ids.push(id); names.push(name || id); }
    };
    (result?.uncovered_actives || []).forEach((u) => push(u.ingredient_id, u.ingredient_name));
    (result?.ingredient_chips || []).filter((c) => c.missing)
      .forEach((c) => push(c.ingredient_id, c.user_input_name || c.name));
    return { ingredientIds: ids, ingredientNames: names };
  }, [result]);

  const ingLabel = ingredientNames[0] || "this ingredient";

  const submit = async () => {
    const e = email.trim();
    if (!e) return;
    setBusy(true);
    try {
      await subscribeGapNotify({
        email: e,
        medication_id: med.id || undefined,
        medication_name: medName,
        ingredient_ids: ingredientIds,
        ingredient_names: ingredientNames,
        medication_query: med.id ? undefined : medName,
      });
      setDone(true);
    } catch (_err) {
      toast.error("Couldn't save that — check the email and try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="gap-capture-done"
        className="mt-4 rounded-2xl border border-safe-border bg-safe-bg/60 px-5 py-4 flex items-start gap-3"
      >
        <Check className="w-5 h-5 text-safe-deep shrink-0 mt-0.5" strokeWidth={2.5} />
        <p className="text-[14px] text-safe-deep leading-relaxed">
          We'll email you when <span className="font-semibold">{medName}</span> ×{" "}
          <span className="font-semibold">{ingLabel}</span> is verified.
        </p>
      </div>
    );
  }

  return (
    <div
      data-testid="gap-capture-box"
      className="mt-4 rounded-2xl border border-surface-line bg-white px-5 py-5 space-y-3"
    >
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center justify-center w-8 h-8 rounded-xl bg-cream-100 border border-surface-line text-ink-soft shrink-0">
          <BellRing className="w-4 h-4" />
        </span>
        <h3 className="text-[15px] font-bold text-ink">Want the answer when it's ready?</h3>
      </div>
      <p className="text-[13.5px] text-ink-soft leading-relaxed">
        Meghana verifies every interaction against the FDA label. We'll email you once,
        when this one is done.
      </p>
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          data-testid="gap-capture-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
          placeholder="you@email.com"
          className="w-full sm:flex-1 h-11 px-3.5 rounded-xl border border-surface-line bg-cream-50 text-[14px] text-ink placeholder:text-ink-light focus:outline-none focus:ring-2 focus:ring-peach-100"
        />
        <button
          type="button"
          data-testid="gap-capture-submit"
          onClick={submit}
          disabled={busy || !email.trim()}
          className="h-11 px-5 rounded-xl bg-ink text-cream-50 hover:bg-ink-soft text-[14px] font-bold transition-colors disabled:opacity-60 whitespace-nowrap"
        >
          {busy ? "Saving…" : "Notify me"}
        </button>
      </div>
      <p className="text-[12px] text-ink-mute">One email. No newsletter.</p>
    </div>
  );
}
